import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import React, { useEffect, useState } from "react";
import CustomTypography from "components/common/CustomTypography";
import { useMutation } from "@tanstack/react-query";
import { useOnboardingPromotionPage } from "../../../../hooks/useOnboardingPromotionPage";
import useMixpanelProfileMkt from "../../../../hooks/useMixpanelProfileMkt";
import { webFunnelApi } from "../../../../api/webFunnelApi";
import { captureException } from "../../../../utils/sentry";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";
import mixpanel from "mixpanel-browser";
import { OnboardingDemographicType } from "../../../../api/onboardingApi";

interface SelectOption {
  id: number;
  label: string;
}

export const selectOptionList: SelectOption[] = [
  { id: 1, label: "남성" },
  { id: 2, label: "여성" },
  { id: 3, label: "논바이너리 (Non-binary)" },
];

export default function Demographic_2_V6({
  version = "v6",
}: {
  version?: MktVersionTypes;
}) {
  const [, setIsAnimating] = useState(false); // 애니메이션 상태 추가

  const pageKey = "demographic_2";

  const { singleSelectionId, updateSingleSelection, handleNext } =
    useOnboardingPromotionPage({
      pageKey,
      nextPageRoute: `/${versionToPathPrefix(version)}-onboarding/demographic/3`,
    });

  const setPromotionPeople = useMixpanelProfileMkt(version);

  // Session storage에서 데이터 복원
  useEffect(() => {
    const storedData = sessionStorage.getItem("demographic_data");
    if (storedData) {
      try {
        const demographicData: OnboardingDemographicType =
          JSON.parse(storedData);
        if (demographicData.genderId) {
          updateSingleSelection(demographicData.genderId);
        }
      } catch (error) {
        console.error("Failed to parse demographic data:", error);
      }
    }
  }, []);

  const { mutate: submitGender } = useMutation(
    ({ distinctId, gender }: { distinctId: string; gender: string }) =>
      webFunnelApi.submitGender(distinctId, gender),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_submit_gender" },
          extra: { pageKey, gender: variables?.gender },
        });
      },
    }
  );

  function handleSubmit(optionId: number) {
    const selectedOption = selectOptionList.find((e) => e.id === optionId);

    if (selectedOption && selectedOption.label) {
      // Set Mixpanel People property
      setPromotionPeople({ 성별: selectedOption.label });

      const distinctId =
        localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
        (mixpanel as any).promotion?.get_distinct_id();

      if (distinctId) {
        submitGender({ distinctId, gender: selectedOption.label });
      } else {
        console.warn(
          `[${pageKey}] WEB_FUNNEL_DISTINCT_ID를 찾지 못해 gender submit을 건너뜁니다.`
        );
      }
    }
  }

  const handleOptionChange = (optionId: number) => {
    updateSingleSelection(optionId);

    // Session storage 업데이트
    const storedData = sessionStorage.getItem("demographic_data");
    const demographicData: OnboardingDemographicType = storedData
      ? JSON.parse(storedData)
      : {};
    demographicData.genderId = optionId;
    sessionStorage.setItem("demographic_data", JSON.stringify(demographicData));

    handleSubmit(optionId);

    setTimeout(() => {
      setIsAnimating(false);
      handleNext({ singleSelectionId: optionId });
    }, 300);
  };

  return (
    <Stack spacing={5} sx={{ height: "100%" }}>
      <Stack spacing={2}>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"성별과 호르몬은 우리 마음에 큰 영향을 미치곤 합니다."}
        </CustomTypography>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"성별이 어떻게 되나요?"}
        </CustomTypography>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            whiteSpace: "nowrap",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {selectOptionList.map(({ id, label }, optionIndex) => (
            <Stack key={`select_option_${id}`} sx={{ flexGrow: 1, gap: 1 }}>
              <Button
                fullWidth
                variant={"SingleSlim"}
                size={"lg"}
                color={singleSelectionId === id ? "primary" : "secondary"}
                sx={{
                  transition: "background-color 0.3s ease", // ease 애니메이션 추가
                }}
                onClick={() => {
                  handleOptionChange(id);
                }}
              >
                {label}
              </Button>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}
